import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import './searchScreen.css';
import { getLocalItem, setLocalItem, removeLocalItem } from '../../../shared/utils/config/localStorage';
import { useNavigation } from '../../../shared/utils/useNavigation';
import { getContentCreatorList } from '../../../shared/actions/contentAction';
import { fetchFormattedCategoriesWithCount } from '../../../shared/actions/categoryAction';
import SearchResultCard from '../../../features/cards/SearchResultCard';
import RecentResultCard from '../../../features/cards/RecentResultCard';
import { getRandomItems, isUserLoggedIn, getUserId, formatProfileTag } from '../../../shared/utils/function';
import { markSearchScreenOpen, getSavedSearchQuery } from '../../../shared/utils/navigationState';
import { resolveVatRate } from '../../../shared/utils/paymentUtil';
import { reportError } from '../../../shared/sentry';

// Constants
const CREATORS_LIMIT = 500;
const MAX_RECENT_SEARCHES = 5;
const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_DELAY = 300;
const INPUT_FOCUS_DELAY = 100;

const SearchScreen = ({
  isOpen,
  onClose,
  onSearchResults = null
}) => {
  const navigate = useNavigation();
  const isLoggedIn = isUserLoggedIn();
  const userId = useMemo(() => getUserId(), []);
  const userInfo = useSelector((state) => state.user.user);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [allCreators, setAllCreators] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [featuredCreators, setFeaturedCreators] = useState([]);
  const [trendingCreators, setTrendingCreators] = useState([]);
  const [creators, setCreators] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // All categories fetched on page load
  const [matchedCategories, setMatchedCategories] = useState([]); // Categories matched with search query
  const [searchLoading, setSearchLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [vatPercentage, setVatPercentage] = useState(0);
  const searchInputRef = useRef(null);
  const isMountedRef = useRef(true);

  // Effect to fetch VAT percentage
  useEffect(() => {
    const fetchVatPercentage = async () => {
      const vatPercentage = await resolveVatRate(userInfo);
      setVatPercentage(vatPercentage);
    };
    fetchVatPercentage();
  }, [userInfo]);

  
  // Normalize items array from different response shapes
  const normalizeItemsArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  };

  // Clear all search state
  const clearSearchState = () => {
    setSearchLoading(false);
    setAllCreators([]);
    setFeaturedCreators([]);
    setTrendingCreators([]);
    setCreators([]);
    setMatchedCategories([]);
  };

  const isLoading = initialLoading || searchLoading;

  const fetchCreators = async (query = '', isInitial = false) => {
    const trimmedQuery = query?.trim() || '';

    if (trimmedQuery.length > 0 && trimmedQuery.length < MIN_SEARCH_LENGTH) {
      if (isMountedRef.current) {
        clearSearchState();
        setInitialLoading(false);
      }
      return;
    }

    if (!isMountedRef.current) return;

    if (isInitial) {
      setInitialLoading(true);
    } else if (trimmedQuery.length > 0) {
      setSearchLoading(true);
    }

    try {
      const params = { limit: CREATORS_LIMIT };
      if (trimmedQuery) {
        params.searchQuery = trimmedQuery;
      } else {
        params.isTrending = true;
        params.isFeature = true;
      }

      if (isLoggedIn) {
        params.userId = userId;
      }

      const result = await getContentCreatorList(params);

      if (!isMountedRef.current) return;

      if (result.success && result.data) {
        const items = normalizeItemsArray(result.data);
        const allCreators = items.map((item) => item.creator || item);
        setAllCreators(allCreators);

        if (trimmedQuery) {
          setFeaturedCreators([]);
          setTrendingCreators([]);
          setCreators(allCreators);
        } else {
          let featuredCreatorsList = allCreators.filter(creator => creator.isFeature === true);
          let trendingCreatorsList = allCreators.filter(creator => creator.isTrending === true);
          let regularCreatorsList = allCreators.filter(
            creator => creator.isFeature !== true && creator.isTrending !== true
          );

          featuredCreatorsList = getRandomItems(featuredCreatorsList, 20);
          trendingCreatorsList = getRandomItems(trendingCreatorsList, 3);
          regularCreatorsList = getRandomItems(regularCreatorsList, 3);

          setFeaturedCreators(featuredCreatorsList);
          setTrendingCreators(trendingCreatorsList);
          setCreators(regularCreatorsList);
          setMatchedCategories([]);
        }

        setInitialLoading(false);
        setSearchLoading(false);

        if (trimmedQuery && onSearchResults) {
          onSearchResults(allCreators);
        }
      } else {
        setInitialLoading(false);
        setSearchLoading(false);
      }
    } catch (error) {
      reportError(error, 'searchScreen fetch creators');
      if (isMountedRef.current) {
        setInitialLoading(false);
        setSearchLoading(false);
        if (trimmedQuery) {
          clearSearchState();
        }
      }
    }
  };

  const fetchCategories = async () => {
    try {
      const isGuest = !isLoggedIn;
      const result = await fetchFormattedCategoriesWithCount(isGuest);

      if (result.success && result.data && Array.isArray(result.data)) {
        setAllCategories(result.data);
      } else {
        setAllCategories([]);
      }
    } catch (error) {
      reportError(error, 'searchScreen fetch categories');
      setAllCategories([]);
    }
  };

  const filterCategories = (query) => {
    const trimmedQuery = query?.trim() || '';

    if (trimmedQuery.length <= 1) {
      setMatchedCategories([]);
      return;
    }

    const searchLower = trimmedQuery.toLowerCase();
    const matched = allCategories.filter(category => {
      const categoryNameLower = category.name.toLowerCase();
      return categoryNameLower.includes(searchLower);
    });

    setMatchedCategories(matched);
  };

  const saveToRecentSearches = (query, topResult) => {
    const recent = getLocalItem('recentSearches', []) || [];
    const newSearch = {
      id: Date.now(),
      query,
      creator: topResult,
      timestamp: Date.now()
    };

    const filtered = recent.filter(item => item.creator.username !== topResult.username);
    const updated = [newSearch, ...filtered].slice(0, MAX_RECENT_SEARCHES);

    setLocalItem('recentSearches', updated);
    setRecentSearches(updated);
  };

  const saveCategoryToRecentSearches = (category) => {
    const recent = getLocalItem('recentSearches', []) || [];
    
    const categoryAsCreator = {
      isCategory: true,
      categoryName: category.name,
      categoryCount: category.count
    };

    const newSearch = {
      id: Date.now(),
      query: category.name,
      creator: categoryAsCreator,
      timestamp: Date.now()
    };

    const filtered = recent.filter(item => 
      !item.creator.isCategory || item.creator.categoryName !== category.name
    );
    const updated = [newSearch, ...filtered].slice(0, MAX_RECENT_SEARCHES);

    setLocalItem('recentSearches', updated);
    setRecentSearches(updated);
  };

  const handleCategoryClick = (category) => {
    if (category) {
      saveCategoryToRecentSearches(category);
      markSearchScreenOpen(searchQuery);
      onClose();
      navigate.toAllCreators(category.name.toLowerCase());
    }
  };
  
  const handleItemClick = (item) => {
    if (item) {
      markSearchScreenOpen(searchQuery);
      onClose();
      if (item.isCategory) {
        navigate.toAllCreators(item.categoryName);
      } else {
        navigate.toProfile(item.username);
      }
    }
  };

  const handleCreatorClick = (creator) => {
    if (creator) {
      const query = `@${creator.username}`;
      saveToRecentSearches(query, creator);
      markSearchScreenOpen(searchQuery);
      onClose();
    }
  };

  const loadRecentSearches = () => {
    const recent = getLocalItem('recentSearches', []);
    setRecentSearches(recent);
  };

  const clearRecentSearches = () => {
    removeLocalItem('recentSearches');
    setRecentSearches([]);
  };

  useEffect(() => {
    if (!isOpen || isInitialLoad) return;

    const timeoutId = setTimeout(() => {
      const trimmedQuery = searchQuery?.trim() || '';

      if (trimmedQuery.length > 1) {
        filterCategories(trimmedQuery);
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, isOpen, allCategories]);

  useEffect(() => {
    if (!isOpen || isInitialLoad) return;

    const timeoutId = setTimeout(() => {
      const trimmedQuery = searchQuery?.trim() || '';

      if (trimmedQuery.length === 0) {
        clearSearchState();
        fetchCreators('');
      } else {
        fetchCreators(trimmedQuery);
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, isOpen]);

  useEffect(() => {
    if (isOpen && isInitialLoad) {
      const savedQuery = getSavedSearchQuery();
      if (savedQuery) {
        setSearchQuery(savedQuery);
        fetchCreators(savedQuery, true);
      } else {
        fetchCreators('', true);
      }
      fetchCategories();
      loadRecentSearches();
      setIsInitialLoad(false);

      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, INPUT_FOCUS_DELAY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isInitialLoad]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isOpen && isMountedRef.current) {
      setSearchLoading(false);
      setInitialLoading(false);
      setIsInitialLoad(true);
      const savedQuery = sessionStorage.getItem('search_screen_query');
      if (!savedQuery) {
        setSearchQuery('');
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="jh-modern-search-screen">
      <header className="jh-search-header">
        <div className="jh-search-header-content">
          <button
            className="modern-legal-back-btn"
            onClick={() => {
              onClose();
              navigate.toDiscover();
            }}
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <div className="jh-search-input-container">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search creators or categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="jh-search-input"
            />
            <i className="fas fa-search jh-search-input-icon"></i>
          </div>
        </div>
      </header>

      <main className="jh-search-main">
        <div className="jh-search-content">
          {isLoading ? (
            <div className="jh-search-loading">
              <div className="jh-search-spinner"></div>
              <span>
                {initialLoading ? 'Loading creators...' : 'Searching creators or categories...'}
              </span>
            </div>
          ) : (
            <>
              {!searchQuery && recentSearches.length > 0 && (
                <div className="jh-search-section">
                  <div className="jh-search-section-header mb-2">
                    <div className="jh-search-section-title-row mb-0">
                      <span>
                        <i className="fas fa-clock jh-search-section-icon"></i>
                      </span>
                      <span className="jh-search-section-title">Recent</span>
                    </div>
                    <button
                      className="jh-search-clear-btn"
                      onClick={clearRecentSearches}
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="jh-search-results-list">
                    {recentSearches.map((item) => (
                      <RecentResultCard
                        key={item.id}
                        item={item.creator}
                        vatPercentage={vatPercentage}
                        showIcon={false}
                        onClick={handleItemClick}
                      />
                    ))}
                  </div>
                </div>
              )}

              {!searchQuery && featuredCreators.length > 0 && (
                <div className="jh-search-section">
                  <div className="jh-search-section-header">
                    <div className="jh-search-section-title-row">
                      <span>
                        <i className="fas fa-star jh-search-section-icon jh-featured-icon"></i>
                      </span>
                      <span className="jh-search-section-title">
                        Featured Creators
                      </span>
                    </div>
                    <button
                      className="jh-search-view-all-btn"
                      onClick={() => navigate.toFeaturedCreators()}
                    >
                      View all
                    </button>
                  </div>
                  <div className="jh-search-results-list">
                    {featuredCreators.map((creator) => (
                      <SearchResultCard
                        key={creator._id}
                        creator={creator}
                        vatPercentage={vatPercentage}
                        avatarClassName="jh-featured-avatar"
                        showIcon={false}
                        onClick={handleCreatorClick}
                      />
                    ))}
                  </div>
                </div>
              )}

              {!searchQuery && trendingCreators.length > 0 && (
                <div className="jh-search-section">
                  <div className="jh-search-section-header">
                    <div className="jh-search-section-title-row">
                      <span>
                        <i className="fas fa-fire jh-search-section-icon jh-trending-icon"></i>
                      </span>
                      <span className="jh-search-section-title">
                        Trending Creators
                      </span>
                    </div>
                    <button
                      className="jh-search-view-all-btn"
                      onClick={() => navigate.toTrendingCreators()}
                    >
                      View all
                    </button>
                  </div>
                  <div className="jh-search-results-list">
                    {trendingCreators.map((creator) => (
                      <SearchResultCard
                        key={creator._id}
                        creator={creator}
                        vatPercentage={vatPercentage}
                        avatarClassName="jh-trending-avatar"
                        showIcon={false}
                        onClick={handleCreatorClick}
                      />
                    ))}
                  </div>
                </div>
              )}

              {searchQuery && creators.length > 0 && searchQuery.trim().length >= MIN_SEARCH_LENGTH && (
                <div className="jh-search-section">
                  <div className="jh-search-section-header">
                    <div className="jh-search-section-title-row">
                      <span className="jh-search-section-title">
                        Creators
                      </span>
                    </div>
                  </div>
                  <div className="jh-search-results-list">
                    {creators.map((creator) => (
                      <SearchResultCard
                        key={creator._id}
                        creator={creator}
                        vatPercentage={vatPercentage}
                        avatarClassName={creator.isTrending ? "jh-trending-avatar" : ""}
                        onClick={handleCreatorClick}
                      />
                    ))}
                  </div>
                </div>
              )}

              {searchQuery && matchedCategories.length > 0 && searchQuery.trim().length > 1 && (
                <div className="jh-search-section">
                  <div className="jh-search-section-header">
                    <div className="jh-search-section-title-row">
                      <span className="jh-search-section-title">Categories</span>
                    </div>
                  </div>
                  <div className="jh-search-results-list">
                    {matchedCategories.map((category, index) => (
                      <div
                        key={`category-${category.name}-${index}`}
                        className="jh-search-result-item jh-search-category-item"
                        onClick={() => handleCategoryClick(category)}
                      >
                        <div className="jh-search-result-info ml-1">
                          <p className="jh-search-result-username">
                            {formatProfileTag(category.name)}
                          </p>
                          {category.count !== undefined && (
                            <p className="jh-search-result-price">
                              {category.count} {category.count === 1 ? 'creator' : 'creators'}
                            </p>
                          )}
                        </div>
                        <i className="fas fa-chevron-right jh-search-category-arrow"></i>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {searchQuery && searchQuery.trim().length > 0 && searchQuery.trim().length < MIN_SEARCH_LENGTH && (
                <div className="jh-search-placeholder">
                  <p className="jh-search-placeholder-text">
                    Type at least {MIN_SEARCH_LENGTH} characters to search...
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default SearchScreen;
